import { formatearTelefono, type ClienteVista } from '@barbones/shared';
import { useQuery } from '@tanstack/react-query';
import { Search, UserPlus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '@/api/cliente';
import { Button } from '@/components/ui/button';
import { Campo, Input } from '@/components/ui/form';
import { SelectOrigen } from './SelectOrigen';
import { useOrigenes } from './useOrigenes';

export interface ClienteNuevo {
  nombre: string;
  telefono: string;
  origen: string;
  origenDetalle?: string;
}

export type SeleccionCliente = { tipo: 'existente'; cliente: ClienteVista } | { tipo: 'nuevo'; datos: ClienteNuevo } | null;

interface Props {
  valor: SeleccionCliente;
  onCambiar: (valor: SeleccionCliente) => void;
  errores?: Record<string, string>;
}

/** Busca un cliente por nombre o teléfono; si no existe, captura sus datos (nombre, teléfono y cómo nos encontró). */
export function SelectorCliente({ valor, onCambiar, errores = {} }: Props) {
  const [busqueda, setBusqueda] = useState('');
  const [diferida, setDiferida] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDiferida(busqueda.trim()), 250);
    return () => clearTimeout(t);
  }, [busqueda]);

  const resultados = useQuery({
    queryKey: ['clientes', 'buscar', diferida],
    queryFn: () => api<ClienteVista[]>('/clientes', { query: { q: diferida } }),
    enabled: diferida.length >= 2 && valor === null,
  });
  const primerOrigen = useOrigenes().activos[0]?.clave;
  // Si se abrió el alta antes de cargar la lista, toma el primer origen en cuanto llega.
  const faltaOrigen = valor?.tipo === 'nuevo' && !valor.datos.origen;
  useEffect(() => {
    if (faltaOrigen && primerOrigen && valor?.tipo === 'nuevo') onCambiar({ tipo: 'nuevo', datos: { ...valor.datos, origen: primerOrigen } });
  }, [faltaOrigen, primerOrigen]);

  if (valor?.tipo === 'existente') {
    const c = valor.cliente;
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface-alt px-3 py-2.5">
        <div className="min-w-0">
          <p className="truncate text-[14px] font-medium">{c.nombre}</p>
          <p className="cifras text-[12.5px] text-foreground-muted">
            {formatearTelefono(c.telefono)} · {c.citasTotal} citas · {c.cancelaciones} cancelaciones · {c.retardos} retardos
          </p>
        </div>
        <Button variante="fantasma" tamano="icono" onClick={() => onCambiar(null)} aria-label="Cambiar cliente">
          <X />
        </Button>
      </div>
    );
  }

  if (valor?.tipo === 'nuevo') {
    const d = valor.datos;
    const set = (cambios: Partial<ClienteNuevo>) => onCambiar({ tipo: 'nuevo', datos: { ...d, ...cambios } });
    return (
      <div className="grid gap-3 rounded-lg border border-dashed border-accent/50 bg-accent-soft/30 p-3 sm:grid-cols-2">
        <div className="flex items-center justify-between sm:col-span-2">
          <p className="text-[13px] font-medium">Cliente nuevo</p>
          <Button variante="fantasma" tamano="sm" onClick={() => onCambiar(null)}>
            Buscar existente
          </Button>
        </div>
        <Campo etiqueta="Nombre completo" requerido error={errores['clienteNuevo.nombre']}>
          {(p) => <Input {...p} value={d.nombre} onChange={(e) => set({ nombre: e.target.value })} autoFocus />}
        </Campo>
        <Campo etiqueta="Teléfono (WhatsApp)" requerido error={errores['clienteNuevo.telefono']}>
          {(p) => <Input {...p} type="tel" inputMode="tel" value={d.telefono} onChange={(e) => set({ telefono: e.target.value })} />}
        </Campo>
        <Campo etiqueta="¿Cómo nos encontró?" requerido>
          {(p) => (
            <SelectOrigen {...p} valor={d.origen} onCambiar={(origen) => set({ origen })} />
          )}
        </Campo>
        <Campo etiqueta="Detalle (opcional)" ayuda="Ej. quién lo recomendó">
          {(p) => <Input {...p} value={d.origenDetalle ?? ''} onChange={(e) => set({ origenDetalle: e.target.value })} />}
        </Campo>
      </div>
    );
  }

  const nombreSugerido = /\d/.test(busqueda) ? '' : busqueda.trim();
  const telefonoSugerido = /\d/.test(busqueda) ? busqueda.trim() : '';

  return (
    <div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground-muted" aria-hidden />
        <Input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por nombre o teléfono…"
          className="pl-9"
          aria-label="Buscar cliente"
          autoFocus
        />
      </div>
      {errores.clienteId && <p className="mt-1 text-[12.5px] text-destructive">{errores.clienteId}</p>}
      <ul className="mt-2 flex max-h-56 flex-col gap-1 overflow-y-auto" aria-label="Resultados de clientes">
        {resultados.data?.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => onCambiar({ tipo: 'existente', cliente: c })}
              className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left hover:bg-muted"
            >
              <span className="text-[13.5px] font-medium">{c.nombre}</span>
              <span className="cifras text-[12.5px] text-foreground-muted">{formatearTelefono(c.telefono)}</span>
            </button>
          </li>
        ))}
      </ul>
      {diferida.length >= 2 && resultados.data?.length === 0 && (
        <p className="px-1 py-2 text-[13px] text-foreground-muted">No hay clientes con “{diferida}”.</p>
      )}
      <Button
        variante="secundario"
        tamano="sm"
        className="mt-2"
        onClick={() => onCambiar({ tipo: 'nuevo', datos: { nombre: nombreSugerido, telefono: telefonoSugerido, origen: primerOrigen ?? '' } })}
      >
        <UserPlus /> Registrar cliente nuevo
      </Button>
    </div>
  );
}
